import { ServerMessage, Success } from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

/**
 * Unwrap a `ServerMessage` into its `success` payload, throwing when it carries
 * an `error` instead.
 *
 * The backend answers a rejected request with HTTP 200 and a `ServerMessage`
 * whose oneof is `error`, not `success` — a batch that failed every command
 * looks exactly like a transport-level success to axios. The previous
 * `data.success as Success` cast therefore produced `undefined` on failure
 * without throwing, so the caller's `catch` never ran and the server's
 * explanation was discarded: a cancel that was refused outright (inactive
 * account, denied permission, disabled venue) left the UI completely silent.
 *
 * Throws `ErrorResponse` so it lands in the same `catch` as a real HTTP error.
 * `status` is 200 because that is genuinely what the server returned — the
 * failure is in the payload, not the transport.
 */
export function unwrapServerMessage(data: ServerMessage): Success {
  if (data?.error) {
    const error: ErrorResponse = { status: 200, message: data.error.message }
    throw error
  }
  return data?.success as Success
}
